import { createTheme, Theme } from '@mollybet/ui';
import mollyLogoWhite from 'common/images/molly-white.svg';
import mollyLogoBlack from 'common/images/molly-black.svg';

/**

   * **This interface has been extended by us; see theme file**
   */
interface DefaultTheme extends Theme {
  /** The primary brand colour */
  primary: string;
  /** Colour used for disabled elements */
  disabled: string;
  /** Colour used for alerting the user of potentially "dangerous" operations */
  warning: string;
  /** Colour used for "dangerous" operations (eg, deletion) */
  danger: string;
  /** The starting neutral colour (or the basic background colour) */
  background: string;
  /** The final neutral colour, or the colour of all text */
  foreground: string;
  /** Colour used for hyperlinks */
  hyperlink: string;
  /** Colours used for UI cards (Higher is more elevated)*/
  cardLevel1: string;
  cardLevel2: string;

  /**
   * Menu colours.
   */
  menu: {
    background: string;
    foreground: string;
  };
  /**
   * Sub Menu colours
   */
  subMenu: {
    background: '#181D1F';
    foreground: '#FFFFFF';
  };

  /**
   * The fonts
   */
  fonts: {
    /** The font applied to html/body */
    base: string;
    /** The font applied to any button element */
    button: string;
    /** The font applied to input labels */
    label: string;
    /** The font applied to the contents of text input elements */
    input: string;
    /** The font applied to the help section under input elements */
    help: string;
    /** The font applied to headings */
    headings: string;
  };

  /**
   * The theme-reliant image URLs
   */
  images: {
    /** Molly logo (black or white) */
    molly: string;
  };

  /** Colour to apply to the modal backdrop */
  modalBackdrop: string;
  /** Colour of the modal close icon */
  modalCloseIconColor: string;
  /** Nice shadows for components with elevation */
  shadows: string[];
}

declare module 'styled-components' {
  /**

   * **This interface has been extended by us; see theme file**
   */
  interface DefaultTheme extends Theme {
    /** The primary brand colour */
    primary: string;
    /** Colour used for disabled elements */
    disabled: string;
    /** Colour used for alerting the user of potentially "dangerous" operations */
    warning: string;
    /** Colour used for "dangerous" operations (eg, deletion) */
    danger: string;
    /** The starting neutral colour (or the basic background colour) */
    background: string;
    /** The final neutral colour, or the colour of all text */
    foreground: string;
    /** Colour used for hyperlinks */
    hyperlink: string;
    /** Colours used for UI cards (Higher is more elevated)*/
    cardLevel1: string;
    cardLevel2: string;

    /**
     * Menu colours.
     */
    menu: {
      background: string;
      foreground: string;
    };
    /**
     * Sub Menu colours
     */
    subMenu: {
      background: '#181D1F';
      foreground: '#FFFFFF';
    };

    /**
     * The fonts
     */
    fonts: {
      /** The font applied to html/body */
      base: string;
      /** The font applied to any button element */
      button: string;
      /** The font applied to input labels */
      label: string;
      /** The font applied to the contents of text input elements */
      input: string;
      /** The font applied to the help section under input elements */
      help: string;
      /** The font applied to headings */
      headings: string;
    };

    /**
     * The theme-reliant image URLs
     */
    images: {
      /** Molly logo (black or white) */
      molly: string;
    };

    /** Colour to apply to the modal backdrop */
    modalBackdrop: string;
    /** Colour of the modal close icon */
    modalCloseIconColor: string;
    /** Nice shadows for components with elevation */
    shadows: string[];
  }
}

const darkTheme = createTheme<DefaultTheme>({
  primary: '#40E2A9',
  disabled: '#A7A7A7',
  warning: '#F89406',
  danger: '#e17074',
  background: '#303030',
  foreground: '#FFFFFF',
  hyperlink: '#74BBFB',
  cardLevel1: '#414141',
  cardLevel2: '#4E4E4E',
  menu: {
    background: '#020D11',
    foreground: '#FFFFFF',
  },
  subMenu: {
    background: '#181D1F',
    foreground: '#FFFFFF',
  },
  images: { molly: mollyLogoWhite },
});

const lightTheme = createTheme<DefaultTheme>({
  primary: '#1cba83',
  disabled: '#A7A7A7',
  warning: '#F89406',
  danger: '#DD5C61',
  background: '#FFFFFF',
  foreground: '#000000',
  hyperlink: '#74BBFB',
  cardLevel1: '#EFEFEF',
  cardLevel2: '#DEDEDE',
  menu: {
    background: '#020D11',
    foreground: '#FFFFFF',
  },
  subMenu: {
    background: '#181D1F',
    foreground: '#FFFFFF',
  },
  images: { molly: mollyLogoBlack },
});

export { lightTheme, darkTheme };
